import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import styled from '@emotion/styled';
import { LoadMoreButton } from '@edusense/shared-ui-kit';
const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const ListContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;
const MessageItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0.5rem 1rem;
  gap: 0.75rem;
  flex-direction: ${(props)=>props.isOwn ? 'row-reverse' : 'row'};
`;
const MessageContent = styled.div`
  max-width: 70%;
  display: flex;
  flex-direction: column;
  align-items: ${(props)=>props.isOwn ? 'flex-end' : 'flex-start'};
`;
const MessageBubble = styled.div`
  background-color: ${(props)=>props.isOwn ? 'var(--theme-primary, #10b981)' : 'var(--theme-paper-hover, #f9fafb)'};
  color: ${(props)=>props.isOwn ? 'white' : 'var(--theme-text, #111827)'};
  padding: 0.75rem 1rem;
  border-radius: ${(props)=>props.isOwn ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem'};
  word-break: break-word;
  white-space: pre-wrap;
  font-size: 0.875rem;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  .dark & {
    background-color: ${(props)=>props.isOwn ? 'var(--theme-primary, #10b981)' : 'var(--theme-paper-hover, #374151)'};
    color: ${(props)=>props.isOwn ? 'white' : 'var(--theme-text, #f3f4f6)'};
  }
`;
const MessageTime = styled.div`
  font-size: 0.75rem;
  color: var(--theme-text-secondary, #6b7280);
  margin-top: 0.25rem;
  text-align: ${(props)=>props.isOwn ? 'right' : 'left'};

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);
  }
`;
const Avatar = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: var(--theme-primary, #10b981);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  flex-shrink: 0;
`;
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--theme-text-secondary, #6b7280);
  text-align: center;
  padding: 2rem;

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);
  }
`;
const MessageRow = ({ index, style, data })=>{
    const { messages, currentUserId, onMessageClick } = data;
    const message = messages[index];
    if (!message) return null;
    const isOwn = message.sender?.userId === currentUserId;
    const initials = message.sender?.displayName?.charAt(0) || message.sender?.userName?.charAt(0) || '?';
    const formatTime = (timestamp)=>{
        const date = new Date(timestamp);
        return date.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        style: style,
        children: /*#__PURE__*/ _jsxs(MessageItem, {
            isOwn: isOwn,
            onClick: ()=>onMessageClick?.(message),
            children: [
                !isOwn && /*#__PURE__*/ _jsx(Avatar, {
                    children: initials.toUpperCase()
                }),
                /*#__PURE__*/ _jsxs(MessageContent, {
                    isOwn: isOwn,
                    children: [
                        /*#__PURE__*/ _jsx(MessageBubble, {
                            isOwn: isOwn,
                            children: message.content
                        }),
                        /*#__PURE__*/ _jsx(MessageTime, {
                            isOwn: isOwn,
                            children: formatTime(message.createdAt)
                        })
                    ]
                }),
                isOwn && /*#__PURE__*/ _jsx(Avatar, {
                    children: initials.toUpperCase()
                })
            ]
        })
    });
};
const VirtualizedMessageList = ({ messages, currentUserId, height, itemSize = 80, onLoadMore, hasMore = false, isLoading = false, onMessageClick, onScrollToBottom, autoScrollToBottom = true })=>{
    const listRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    // 메시지 데이터 메모화
    const messageData = useMemo(()=>({
            messages,
            currentUserId,
            onMessageClick
        }), [
        messages,
        currentUserId,
        onMessageClick
    ]);
    // 새 메시지가 추가될 때 자동 스크롤
    useEffect(()=>{
        if (autoScrollToBottom && isAtBottom && listRef.current) {
            listRef.current.scrollToItem(messages.length - 1, 'end');
        }
    }, [
        messages.length,
        autoScrollToBottom,
        isAtBottom
    ]);
    // 스크롤 이벤트 핸들러
    const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested })=>{
        if (!scrollUpdateWasRequested) {
            const isNearBottom = scrollOffset + height >= messages.length * itemSize - 100;
            setIsAtBottom(isNearBottom);
            // 상단에 도달했을 때 더 많은 메시지 로드
            if (scrollOffset <= 100 && hasMore && !isLoading && onLoadMore) {
                onLoadMore();
            }
        }
    }, [
        height,
        messages.length,
        itemSize,
        hasMore,
        isLoading,
        onLoadMore
    ]);
    // 하단으로 스크롤 함수
    const scrollToBottom = useCallback(()=>{
        if (listRef.current) {
            listRef.current.scrollToItem(messages.length - 1, 'end');
            setIsAtBottom(true);
            onScrollToBottom?.();
        }
    }, [
        messages.length,
        onScrollToBottom
    ]);
    // 메시지가 없는 경우
    if (messages.length === 0) {
        return /*#__PURE__*/ _jsx(Container, {
            children: /*#__PURE__*/ _jsxs(EmptyState, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            fontSize: '3rem',
                            marginBottom: '1rem'
                        },
                        children: "💬"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            marginBottom: '0.5rem'
                        },
                        children: "아직 메시지가 없습니다"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            fontSize: '0.875rem'
                        },
                        children: "첫 번째 메시지를 보내보세요!"
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            hasMore && /*#__PURE__*/ _jsx(LoadMoreButton, {
                onClick: onLoadMore,
                disabled: isLoading,
                children: isLoading ? '로딩 중...' : '이전 메시지 보기'
            }),
            /*#__PURE__*/ _jsx(ListContainer, {
                children: /*#__PURE__*/ _jsx(List, {
                    ref: listRef,
                    height: height - (hasMore ? 50 : 0),
                    itemCount: messages.length,
                    itemSize: itemSize,
                    itemData: messageData,
                    onScroll: handleScroll,
                    style: {
                        overflowX: 'hidden'
                    },
                    children: MessageRow
                })
            }),
            !isAtBottom && /*#__PURE__*/ _jsx("div", {
                style: {
                    position: 'absolute',
                    bottom: '1rem',
                    right: '1rem',
                    zIndex: 10
                },
                children: /*#__PURE__*/ _jsx("button", {
                    onClick: scrollToBottom,
                    style: {
                        width: '3rem',
                        height: '3rem',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: 'var(--theme-primary, #10b981)',
                        color: 'white',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                        transition: 'transform 0.2s'
                    },
                    onMouseEnter: (e)=>e.currentTarget.style.transform = 'scale(1.1)',
                    onMouseLeave: (e)=>e.currentTarget.style.transform = 'scale(1)',
                    children: "↓"
                })
            })
        ]
    });
};
export default VirtualizedMessageList;
